import { CourseDataApi } from "@features/shared/api/course-data.api";
import { MetaPredictionParams, MetaPredictionResponse } from "./entities/meta-prediction";
import { metaPurchaseEventSent } from "./events";

/**
 * Browser-side Meta Pixel events for the paywalls this app still renders (renew, reactivate),
 * mirroring app-funnels: the value Meta gets is the predicted LTV from the admin's prediction
 * sets, never the real plan price, and Meta gets one Purchase per student.
 *
 * - InitiateCheckout at checkout: with the predicted value when the onboarding has a row for
 *   the plan, without a value when it has none, and not at all for a student whose Purchase
 *   Meta already got (`first_purchase: false`).
 * - Purchase after payment: with the predicted value, or the charged price when there is no
 *   prediction (the value the server falls back to as well; the two deduplicate by eventID);
 *   not sent for a deferred row (the server sends the actual sum ~10 min later) nor for a
 *   repeat buyer.
 */

const META_PLAN_STORAGE_KEY = "meta_prediction_plan_code";
/** The signed-in student's email, mirrored by the auth model (see persistMetaPredictionEmail). */
const META_STUDENT_EMAIL_STORAGE_KEY = "meta_prediction_email";
/** The email a funnel collected before sign-up. */
const META_EMAIL_STORAGE_KEY = "email";
/** The onboardings whose purchases this app reports to Meta from the browser. */
const META_PURCHASE_ONBOARDING_IDS = new Set(["renew", "reactivate"]);
let lastFiredInitiateCheckoutPlanCode: string | null = null;

/** The charged price, as `purchaseCompletedSuccess` knows it. */
export type PurchasePriceParams = {
    currency: string;
    price: number;
    /** The price in the checkout currency when it differs from `price` (as GTM reads it). */
    pricecurrency?: number;
    productId?: string;
};

export const persistMetaPredictionPlanCode = (code?: string | null) => {
    if (typeof window === "undefined") return;
    if (!code) return;
    try {
        window.sessionStorage.setItem(META_PLAN_STORAGE_KEY, code);
    } catch (error) {
        console.error("[MetaPrediction] failed to persist plan code", error);
    }
};

/**
 * Remember the signed-in student's email for the prediction request. The app runs its effector
 * stores in a scope (app/Providers.tsx), so `$student.getState()` read from here would be the
 * empty base store; the auth model mirrors the email through this call instead (null on logout).
 */
export const persistMetaPredictionEmail = (email?: string | null) => {
    if (typeof window === "undefined") return;
    try {
        const trimmed = email?.trim();
        if (trimmed) window.sessionStorage.setItem(META_STUDENT_EMAIL_STORAGE_KEY, trimmed);
        else window.sessionStorage.removeItem(META_STUDENT_EMAIL_STORAGE_KEY);
    } catch (error) {
        console.error("[MetaPrediction] failed to persist email", error);
    }
};

const getPlanCodeFromStorage = (): string | null => {
    if (typeof window === "undefined") return null;
    const storedPlanCode = window.sessionStorage.getItem(META_PLAN_STORAGE_KEY);
    if (storedPlanCode) return storedPlanCode;
    try {
        const subscriptionRaw = window.sessionStorage.getItem("subscription");
        if (!subscriptionRaw) return null;
        const subscription = JSON.parse(subscriptionRaw) as { code?: string; id?: string };
        if (subscription?.code) return subscription.code;
        if (subscription?.id) return subscription.id;
    } catch (error) {
        console.error("[MetaPrediction] failed to parse subscription plan code", error);
    }
    return null;
};

const getOnboardingId = (): string | undefined => {
    if (typeof window === "undefined") return undefined;
    const onboarding = window.sessionStorage.getItem("onboarding_id");
    return onboarding ?? undefined;
};

const getStoredEmail = (): string | null => {
    if (typeof window === "undefined") return null;
    const studentEmail = window.sessionStorage.getItem(META_STUDENT_EMAIL_STORAGE_KEY)?.trim();
    if (studentEmail) return studentEmail;
    const storedEmail = window.sessionStorage.getItem(META_EMAIL_STORAGE_KEY)?.trim();
    if (storedEmail) return storedEmail;
    try {
        const subscriptionRaw = window.sessionStorage.getItem("subscription");
        if (!subscriptionRaw) return null;
        const subscription = JSON.parse(subscriptionRaw) as { customer_email?: string };
        const subscriptionEmail = subscription?.customer_email?.trim();
        return subscriptionEmail || null;
    } catch (error) {
        console.error("[MetaPrediction] failed to parse stored subscription email", error);
        return null;
    }
};

const buildMetaPredictionParams = (orderId?: string): MetaPredictionParams | null => {
    if (typeof window === "undefined") return null;
    const email = getStoredEmail();
    const planCode = getPlanCodeFromStorage();
    if (!email || !planCode) return null;
    const params: MetaPredictionParams = { email, plan_code: planCode };
    const onboardingId = getOnboardingId();
    if (onboardingId) params.onboarding_id = onboardingId;
    if (orderId) params.order_id = orderId;
    return params;
};

/** Null on any failure — the browser then sends nothing and leaves the Purchase to the server. */
const fetchMetaPrediction = async (orderId?: string): Promise<MetaPredictionResponse | null> => {
    const params = buildMetaPredictionParams(orderId);
    if (!params) return null;
    try {
        const response = await CourseDataApi.getMetaPrediction(params);
        console.log("[MetaPrediction]", response);
        return response;
    } catch (error) {
        console.error("[MetaPrediction] request failed", error);
        return null;
    }
};

/** The charged price in the checkout currency (`pricecurrency` when present). */
const actualPrice = (purchase?: PurchasePriceParams): { value: number; currency: string } | null => {
    if (!purchase || !purchase.currency) return null;
    const value = typeof purchase.pricecurrency === "number" ? purchase.pricecurrency : purchase.price;
    return typeof value === "number" && value > 0 ? { value, currency: purchase.currency } : null;
};

const trackMetaPixelPurchase = (payload: MetaPredictionResponse, orderId: string, purchase?: PurchasePriceParams) => {
    if (typeof window === "undefined") return;
    const pixel = window.fbq;
    if (typeof pixel !== "function") return;
    // No prediction for this onboarding/plan: Meta still gets the purchase, with the charged
    // price — the value the server falls back to as well (deduplicated by eventID).
    const sent =
        payload.prediction && payload.prediction.value != null
            ? { value: payload.prediction.value, currency: payload.prediction.currency }
            : actualPrice(purchase);
    if (!sent) return;
    try {
        pixel(
            "track",
            "Purchase",
            {
                value: sent.value,
                currency: sent.currency
            },
            {
                eventID: orderId
            }
        );
        metaPurchaseEventSent({
            value: sent.value,
            currency: sent.currency,
            eventId: orderId
        });
    } catch (error) {
        console.error("[MetaPrediction] meta pixel track failed", error);
    }
};

const trackMetaPixelInitiateCheckout = (payload: MetaPredictionResponse | null) => {
    if (typeof window === "undefined") return;
    const pixel = window.fbq;
    if (typeof pixel !== "function") return;
    const predictedValue = payload?.prediction;
    try {
        if (predictedValue && predictedValue.value != null) {
            pixel("track", "InitiateCheckout", {
                value: predictedValue.value,
                currency: predictedValue.currency
            });
        } else {
            // No prediction for this onboarding/plan (no rows) — still send the
            // checkout signal to Meta, just without a value.
            pixel("track", "InitiateCheckout");
        }
    } catch (error) {
        console.error("[MetaPrediction] InitiateCheckout track failed", error);
    }
};

let metaPredictionRequestInFlight = false;

const requestMetaPrediction = async (orderId: string, purchase?: PurchasePriceParams) => {
    if (metaPredictionRequestInFlight) return;
    const onboardingId = getOnboardingId();
    if (!onboardingId || !META_PURCHASE_ONBOARDING_IDS.has(onboardingId)) return;
    metaPredictionRequestInFlight = true;
    try {
        const response = await fetchMetaPrediction(orderId);
        if (!response) return;
        // A deferred row: the server sends the Purchase ~10 min later with the actual charged
        // sum, and a pixel Purchase now would make Meta count a second purchase with a different
        // value. A repeat buyer: Meta gets one Purchase per student, as on the server.
        if (response.deferred || response.first_purchase === false) return;
        trackMetaPixelPurchase(response, orderId, purchase);
    } finally {
        metaPredictionRequestInFlight = false;
    }
};

export const triggerMetaPredictionRequest = (orderId?: string, purchase?: PurchasePriceParams) => {
    if (!orderId) return;
    void requestMetaPrediction(orderId, purchase);
};

export const triggerMetaInitiateCheckoutPrediction = () => {
    if (typeof window === "undefined") return;
    const planCode = getPlanCodeFromStorage();
    if (!planCode) return;
    if (lastFiredInitiateCheckoutPlanCode === planCode) return;
    // Without email/plan the prediction can't even be requested yet — keep the
    // one-shot unconsumed so a later call can still fire with a value.
    if (!buildMetaPredictionParams()) return;
    void (async () => {
        const response = await fetchMetaPrediction();
        // Meta already got this student's Purchase (e.g. an expired subscriber on renew):
        // no checkout signal either, as with the Purchase.
        if (response?.first_purchase !== false) trackMetaPixelInitiateCheckout(response);
        lastFiredInitiateCheckoutPlanCode = planCode;
    })();
};
